import { Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { TemplateAttachment } from 'src/app/core/models/template-attachment/template-attachment.model';
import { TemplateAttachmentApiService } from 'src/app/core/services/template-attachments/template-attachment.service';
import { AlertService } from '../../services/alert/alert.service';

@Component({
  selector: 'app-template-attachments-modal',
  standalone: false,
  encapsulation: ViewEncapsulation.None,
  templateUrl: './template-attachments-modal.component.html',
  styleUrls: ['./template-attachments-modal.component.scss']
})
export class TemplateAttachmentsModalComponent implements OnChanges {
  @Input() open = false;
  @Input() category!: 'tenant' | 'owner' | 'key';
  @Input() categoryLabel!: string;
  @Output() close = new EventEmitter<void>();

  templates: TemplateAttachment[] = [];
  uploading = false;
  loading = false;

  private api = inject(TemplateAttachmentApiService);
  private alert = inject(AlertService);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open'] && this.open && this.category) {
      this.loadTemplates();
    }
  }

  loadTemplates(): void {
    this.loading = true;
    this.api.list(this.category).subscribe({
      next: templates => {
        this.templates = templates;
        this.loading = false;
      },
      error: () => {
        this.alert.error('Falha ao carregar modelos');
        this.loading = false;
      }
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];
    this.uploading = true;

    this.api.upload(this.category, file).subscribe({
      next: template => {
        this.templates = [template, ...this.templates];
        this.uploading = false;
        this.alert.success('Modelo adicionado com sucesso!');
        input.value = '';
      },
      error: err => {
        this.uploading = false;
        const message = err?.message || 'Falha ao enviar modelo';
        this.alert.error(message);
        input.value = '';
      }
    });
  }

  downloadTemplate(template: TemplateAttachment): void {
    this.api.download(template.id).subscribe({
      next: blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = template.name;
        a.click();
        URL.revokeObjectURL(url);
        this.alert.success('Download iniciado');
      },
      error: () => {
        this.alert.error('Falha ao baixar modelo');
      }
    });
  }

  deleteTemplate(template: TemplateAttachment): void {
    if (!confirm(`Deseja realmente excluir "${template.name}"?`)) {
      return;
    }

    this.api.remove(template.id).subscribe({
      next: () => {
        this.templates = this.templates.filter(t => t.id !== template.id);
        this.alert.success('Modelo excluído com sucesso!');
      },
      error: () => {
        this.alert.error('Falha ao excluir modelo');
      }
    });
  }

  getFileIcon(mimeType: string): string {
    if (mimeType.startsWith('image/')) return 'fas fa-file-image';
    if (mimeType.includes('pdf')) return 'fas fa-file-pdf';
    if (mimeType.includes('word')) return 'fas fa-file-word';
    return 'fas fa-file';
  }

  formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  formatDate(isoDate: string): string {
    return new Date(isoDate).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }
}
